
import axios from 'axios'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { useEffect, useState } from 'react'
import { Loading } from 'react-daisyui'
import { GaugeComponent } from 'react-gauge-component'
import { useParams } from 'react-router-dom'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import Swal from 'sweetalert2'
import logo from '../../assets/logo_bp_2025.png'
import cabecera from '../../assets/reporte.png'
import { Cumplimiento } from '../cumplimiento/interface'
import { Tema } from '../encuestado/interface'
import { Encuesta } from '../encuestas/interface'
import styles from './Pdf.module.scss'

const PDF_BACKGROUND = '#000a28';
const PDF_BACKGROUND_RGB = { r: 0, g: 10, b: 40 };
const PDF_EXPORT_PADDING = '20px';
const PDF_BLOCK_GAP = 4;
const EXPORT_BLOCK_IDS = [
	'pagina1-contenido',
	'graficoNivel',
	'graficoResultadoTema',
	'graficoResultadoTotal',
	'cierreReporte'
];

type PdfCursor = {
	hasPages: boolean;
	usedHeight: number;
}

const wrapAxisLabel = (value: string | number) => {
	const words = String(value).split(' ');
	const lines: string[] = [];
	let currentLine = '';

	words.forEach((word) => {
		const nextLine = currentLine ? `${currentLine} ${word}` : word;

		if (nextLine.length > 14 && currentLine) {
			lines.push(currentLine);
			currentLine = word;
		} else {
			currentLine = nextLine;
		}
	});

	if (currentLine) lines.push(currentLine);

	return lines.join('<br/>');
}

const fillPdfPage = (pdf: jsPDF) => {
	const pdfWidth = pdf.internal.pageSize.getWidth();
	const pdfHeight = pdf.internal.pageSize.getHeight();

	pdf.setFillColor(PDF_BACKGROUND_RGB.r, PDF_BACKGROUND_RGB.g, PDF_BACKGROUND_RGB.b);
	pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');
}

const startPdfPage = (pdf: jsPDF, cursor: PdfCursor) => {
	if (cursor.hasPages) pdf.addPage();

	fillPdfPage(pdf);
	cursor.hasPages = true;
	cursor.usedHeight = 0;
}

const addCanvasSlice = (
	pdf: jsPDF,
	canvas: HTMLCanvasElement,
	sourceY: number,
	sourceHeightPx: number,
	targetY: number,
	targetHeight: number,
	pdfWidth: number
) => {
	const sliceCanvas = document.createElement('canvas');
	sliceCanvas.width = canvas.width;
	sliceCanvas.height = sourceHeightPx;
	const context = sliceCanvas.getContext('2d');

	if (!context) return false;

	context.fillStyle = PDF_BACKGROUND;
	context.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
	context.drawImage(
		canvas,
		0,
		sourceY,
		canvas.width,
		sourceHeightPx,
		0,
		0,
		canvas.width,
		sourceHeightPx
	);

	pdf.addImage(
		sliceCanvas.toDataURL('image/png'),
		'PNG',
		0,
		targetY,
		pdfWidth,
		targetHeight
	);

	return true;
}

const addCanvasPages = (pdf: jsPDF, canvas: HTMLCanvasElement, cursor: PdfCursor) => {
	const pdfWidth = pdf.internal.pageSize.getWidth();
	const pdfHeight = pdf.internal.pageSize.getHeight();
	const pixelsByPdfUnit = canvas.width / pdfWidth;
	const canvasHeight = canvas.height / pixelsByPdfUnit;

	if (!cursor.hasPages) startPdfPage(pdf, cursor);

	if (canvasHeight <= pdfHeight) {
		const gap = cursor.usedHeight > 0 ? PDF_BLOCK_GAP : 0;
		const targetY = cursor.usedHeight + gap;

		if (targetY + canvasHeight > pdfHeight) {
			startPdfPage(pdf, cursor);
		}

		const nextGap = cursor.usedHeight > 0 ? PDF_BLOCK_GAP : 0;
		const nextTargetY = cursor.usedHeight + nextGap;

		pdf.addImage(
			canvas.toDataURL('image/png'),
			'PNG',
			0,
			nextTargetY,
			pdfWidth,
			canvasHeight
		);
		cursor.usedHeight = nextTargetY + canvasHeight;

		return true;
	}

	if (cursor.usedHeight > 0) startPdfPage(pdf, cursor);

	let currentY = 0;

	while (currentY < canvas.height) {
		const availableHeight = pdfHeight - cursor.usedHeight;
		const availableHeightPx = Math.floor(availableHeight * pixelsByPdfUnit);

		if (availableHeightPx <= 0) {
			startPdfPage(pdf, cursor);
			continue;
		}

		const sliceHeightPx = Math.min(availableHeightPx, canvas.height - currentY);
		const sliceHeight = sliceHeightPx / pixelsByPdfUnit;
		const added = addCanvasSlice(
			pdf,
			canvas,
			currentY,
			sliceHeightPx,
			cursor.usedHeight,
			sliceHeight,
			pdfWidth
		);

		if (!added) return false;

		currentY += sliceHeightPx;
		cursor.usedHeight += sliceHeight;

		if (currentY < canvas.height) startPdfPage(pdf, cursor);
	}

	return true;
}

export default function Pdf() {
	const [loading, setLoading] = useState(true);
	const [encuesta, setEncuesta] = useState<Encuesta>({});
	const [peso_cuestionario, setPesoCuestionario] = useState<number>(0);
	const [temas, setTemas] = useState<Tema[]>([]);
	//@ts-ignore
	const [nivelCumplimiento, setNivelCumplimiento] = useState<Cumplimiento>({});
	const { cuestionarioId, clienteId } = useParams()
	const [arcos, setArcos] = useState({});

	const toPDF = async () => {
		const pdf = new jsPDF('p', 'mm', 'a4');
		const cursor: PdfCursor = { hasPages: false, usedHeight: 0 };

		for (let i = 0; i < EXPORT_BLOCK_IDS.length; i++) {
			const element = document.getElementById(EXPORT_BLOCK_IDS[i]);
			if (!element) continue;

			const canvas = await html2canvas(element, {
				scale: 2,
				useCORS: true,
				backgroundColor: PDF_BACKGROUND,
				onclone: (clonedDocument) => {
					const clonedElement = clonedDocument.getElementById(EXPORT_BLOCK_IDS[i]);
					if (!clonedElement) return;

					clonedElement.style.boxSizing = 'border-box';
					clonedElement.style.padding = PDF_EXPORT_PADDING;
				}
			});

			const added = addCanvasPages(pdf, canvas, cursor);

			if (!added) return;
		}

		if (!cursor.hasPages) return;

		pdf.save('Nivel_de_cumplimiento.pdf');
	};

	const descargarEncuesta = () => {
		setLoading(true)
		axios.get(`/api/encuestas/${clienteId}/${cuestionarioId}`)
			.then((response) => setEncuesta(response.data))
			.catch((error) => {
				Swal.fire({
					heightAuto: false,
					icon: 'error',
					title: 'Error al obtener la encuesta',
					text: error.response.data.message
				})
			})
	}

	const descargarResultados = () => {
		axios.get(`/api/encuestas/${clienteId}/${cuestionarioId}/resultado-tema`)
			.then((response) => setTemas(response.data))
			.catch((error) => {
				Swal.fire({
					heightAuto: false,
					icon: 'error',
					title: 'Error al obtener los resultados',
					text: error.response.data.message
				})
			});
	}

	const descargarNivelCumplimiento = () => {
		if (!encuesta.id_nivel_cumplimiento) return;
		axios.get(`/api/cuestionarios/${encuesta.id_cuestionario}/cumplimientos/${encuesta.id_nivel_cumplimiento}`)
			.then((response) => setNivelCumplimiento(response.data.cumplimiento))
			.catch((error) => {
				Swal.fire({
					heightAuto: false,
					icon: 'error',
					title: 'Error al obtener el detalle del nivel de cumplimiento',
					text: error.response.data.message
				})
			})
			.finally(() => setLoading(false));
	}

	const obtenerColor = (numero: number) => {
		if (numero < 50) return '#EA4228';
		if (numero < 85) return '#F5CD19';
		return '#5BE12C';
	}

	useEffect(() => {
		descargarEncuesta();
	}, [])

	useEffect(() => {
		descargarResultados();
		descargarNivelCumplimiento();
		setArcos({
			subArcs: [
				{ limit: 50, color: '#EA4228', showTick: true },
				{ limit: 85, color: '#F5CD19', showTick: true },
				{ limit: 100, color: '#5BE12C', showTick: true },
			]
		});
	}, [encuesta])

	useEffect(() => {
		let peso = 0;
		temas.forEach((tema: Tema) => { peso += tema.peso_tema ?? 0; })
		setPesoCuestionario(peso);
	}, [temas]);

	return (
		<div className={styles.encuestas_wrapper}>
			<div className={styles.encuestas_container}>
				<div className={styles.encuestas}>
					{loading ? (
						<div className={styles.loading}><Loading color="primary" /></div>
					) : (
						<>
							<div className={styles.actions}>
								<button onClick={toPDF} className={styles.button_print}>Descargar PDF</button>
							</div>

								<div id="pagina1" className={styles.encuesta} style={{ backgroundColor: '#000a28', padding: '20px'}}>
									<div id="pagina1-contenido" className={styles.export_group}>
										<div className={styles.cabecera}>
											<div className={styles.titulo}>{encuesta.nombre}</div>
											<img src={cabecera} alt="Cabecera" className={styles.cabecera} />
										</div>
										<p className={styles.descripcion}>{encuesta.descripcion}</p>
										<div className={styles.datos_cliente}>
											<h2><u>Fecha fin:</u> {encuesta.fecha_fin_formateado}</h2>
											<h2><u>Empresa:</u> {encuesta.empresa}</h2>
											<h2><u>Usuario:</u> {encuesta.cliente_apellido} {encuesta.cliente_nombre}</h2>
										</div>
										<div className={[styles.nivel, nivelCumplimiento.nombre?.toLowerCase()].join(' ')}>Nivel: {nivelCumplimiento.nombre}</div>
										<div className={styles.introduccion_cierre}>
											<p>{nivelCumplimiento.introduccion}</p>	
										</div>
									</div>
									<div id="graficoNivel" className={styles.export_chart}>
										<GaugeComponent arc={arcos} value={encuesta.resultado} className={styles.grafico_arco} />
									</div>
								</div>

								{Number(encuesta.id_estado) === 3 && (
									<div id="pagina2" className={styles.encuesta} style={{ backgroundColor: '#000a28', padding: '20px'}}>
										<div id="graficoResultadoTema" className={styles.export_chart}>
											<HighchartsReact
												highcharts={Highcharts}
												options={{
													chart: { backgroundColor: 'transparent', height: 900 },
													title: { text: 'Resultado por tema', style: { color: 'currentColor' } },
													xAxis: {
														type: 'category',
														labels: {
															formatter: function (this: { value: string | number }) {
																return wrapAxisLabel(this.value);
															},
															style: { color: '#fff', fontSize: '11px', textOverflow: 'none' }
														}
													},
													yAxis: {
														title: { text: 'Porcentaje de cada tema', style: { color: 'currentColor' } },
														labels: { style: { color: '#fff' } },
														gridLineColor: '#ffffff50'
													},
													legend: { enabled: false },
													series: [{
														type: 'column',
														dataLabels: { enabled: true, format: '{y:.2f}%', style: { color: '#fff', fontWeight: 'bold', textOutline: 'none' } },
														tooltip: { pointFormat: '<b>{point.y:.2f}%</b><br/>' },
														data: temas.map((tema) => ({
															name: tema.nombre_mostrable,
															y: tema.resultado_tema,
															color: obtenerColor(tema.resultado_tema || 0)
														}))
													}]
												}}
											/>
										</div>
										<div id="graficoResultadoTotal" className={styles.export_chart}>
											<HighchartsReact
												highcharts={Highcharts}
												options={{
													chart: { type: 'bar', backgroundColor: 'transparent', height: 900 },
													title: { text: 'Resultado sobre el total', style: { color: 'currentColor' } },
													xAxis: { type: 'category', labels: { style: { color: '#fff' } } },
													yAxis: {
														title: { text: 'Porcentaje sobre el total', style: { color: 'currentColor' } },
														labels: { style: { color: '#fff' } },
														gridLineColor: '#ffffff50'
													},
													legend: { enabled: false },
													series: [
														{
															type: 'column',
															dataLabels: { enabled: true, format: '{y:.2f}%', style: { color: '#fff', fontWeight: 'bold', textOutline: 'none' } },
															tooltip: { pointFormat: '<b>{point.y:.2f}%</b><br/>' },
															data: temas.map((tema) => ({
																name: tema.nombre_mostrable,
																y: tema.resultado_sobre_total,
																color: '#135bc2'
															}))
														},
														{
															type: 'column',
															dataLabels: { enabled: true, format: '{point.y:.2f}%', style: { color: '#fff', fontWeight: 'bold', textOutline: 'none' } },
															tooltip: { pointFormat: '<b>{point.y:.2f}%</b><br/>' },
															data: temas.map((tema) => ({
																name: tema.nombre_mostrable,
																y: ((tema.peso_tema ?? 0) * 100 / peso_cuestionario),
																color: '#441984'
															}))
														}
													]
												}}
											/>
										</div>
										<div id="cierreReporte" className={styles.export_footer}>
											<div className={styles.introduccion_cierre}>
												<p>{nivelCumplimiento.cierre}</p>
											</div>
											<img src={logo} alt="Logo" className={styles.logo} />
										</div>
									</div>
				)}

						</>
					)}
				</div>
			</div>
		</div>
	);
}
